import PropTypes from 'prop-types';
import { Box, Avatar, Divider, Grid, Typography, Card, CardContent } from '@mui/material';

import {
    Favorite,
    TrendingUp,
    TrendingDown,
    CalendarToday,
    AccessTime,
    DirectionsWalk,
    FavoriteBorder
} from '@mui/icons-material';

import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import EqualizerIcon from '@mui/icons-material/Equalizer';

import { t } from 'i18next';
import { formatDateTimeMoment } from 'utils/dateUtils';

const RecordInfo = ({ record, maxHr, minHr, userData }) => {
    const minutes = Math.floor(record.duration / 60);
    const seconds = record.duration % 60;

    return (
        <>
            <Card sx={{ mb: 3 }} elevation={3}>
                <CardContent sx={{ backgroundColor: '#f9f9f9' }}>
                    <Grid container spacing={3} alignItems="flex-start">
                        <Grid item>
                            <Avatar
                                sx={{
                                    width: 200,
                                    height: 200,
                                    bgcolor: '#1976d2',
                                    fontSize: 40
                                }}
                            >
                                {record.patient.name?.charAt(0)}
                            </Avatar>
                        </Grid>

                        <Grid item xs>
                            <Typography
                                variant="h2"
                                fontWeight="bold"
                                gutterBottom
                                sx={{ mb: 2 }}
                            >
                                {record.patient.name}
                            </Typography>

                            <Typography variant="h5" fontWeight="bold" gutterBottom>
                                {t('records.healthData')}
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <Grid container spacing={2} mb={3}>
                                <Grid item xs={12} sm={3}>
                                    <Box display="flex" alignItems="center">
                                        <FavoriteBorderIcon
                                            color="primary"
                                            sx={{ mr: 1 }}
                                        />
                                        <Typography variant="body1">
                                            <strong>{t('records.fcResting')}</strong>{' '}
                                            {userData.restingHeartRate} Bpm
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Box display="flex" alignItems="center">
                                        <FitnessCenterIcon
                                            color="primary"
                                            sx={{ mr: 1 }}
                                        />
                                        <Typography variant="body1">
                                            <strong>{t('records.fcMinTraining')}</strong>{' '}
                                            {userData.minTrainingHeartRate} Bpm
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Box display="flex" alignItems="center">
                                        <FitnessCenterIcon
                                            color="primary"
                                            sx={{ mr: 1 }}
                                        />
                                        <Typography variant="body1">
                                            <strong>{t('records.fcTraining')}</strong>{' '}
                                            {userData.trainingHeartRate} Bpm
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Box display="flex" alignItems="center">
                                        <FavoriteIcon color="primary" sx={{ mr: 1 }} />
                                        <Typography variant="body1">
                                            <strong>{t('records.fcMax')}</strong>{' '}
                                            {userData.maxHeartRate} Bpm
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>

                            <Typography variant="h5" fontWeight="bold" gutterBottom>
                                {t('records.recordData')}
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={3}>
                                    <Box display="flex" alignItems="center">
                                        <TrendingUp color="primary" sx={{ mr: 1 }} />
                                        <Typography variant="body1">
                                            <strong>{t('records.fcMaxRecorded')}</strong>{' '}
                                            {maxHr} Bpm
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Box display="flex" alignItems="center">
                                        <EqualizerIcon color="primary" sx={{ mr: 1 }} />
                                        <Typography variant="body1">
                                            <strong>{t('records.fcAverage')}</strong>{' '}
                                            {record.mHR} Bpm
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Box display="flex" alignItems="center">
                                        <TrendingDown color="primary" sx={{ mr: 1 }} />
                                        <Typography variant="body1">
                                            <strong>{t('records.fcMinRecorded')}</strong>{' '}
                                            {minHr} Bpm
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                    {
                        icon: <CalendarToday sx={{ fontSize: 44, color: '#1976d2' }} />,
                        value: formatDateTimeMoment(record.ti),
                        label: t('records.labels.startDate')
                    },
                    {
                        icon: <CalendarToday sx={{ fontSize: 44, color: '#1976d2' }} />,
                        value: formatDateTimeMoment(record.tf),
                        label: t('records.labels.endDate')
                    },
                    {
                        icon: <AccessTime sx={{ fontSize: 44, color: '#1976d2' }} />,
                        value: `${minutes}m ${seconds}s`,
                        label: t('records.labels.duration')
                    },
                    {
                        icon: <DirectionsWalk sx={{ fontSize: 44, color: '#1976d2' }} />,
                        value: `${record.distance} km`,
                        label: t('records.labels.distance')
                    },
                    {
                        icon: <FavoriteBorder sx={{ fontSize: 44, color: '#1976d2' }} />,
                        value: `64 bpm`,
                        label: t('records.labels.fcRest')
                    },
                    {
                        icon: <Favorite sx={{ fontSize: 44, color: '#1976d2' }} />,
                        value: `15 bpm`,
                        label: t('records.labels.fcRecovery')
                    }
                ].map((stat, i) => (
                    <Grid item xs={12} sm={6} md={2} key={i}>
                        <Card
                            elevation={3}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                p: 2,
                                borderRadius: 3,
                                backgroundColor: '#f9f9f9'
                            }}
                        >
                            <Box mr={2}>{stat.icon}</Box>
                            <Box>
                                <Typography variant="h5" fontWeight={600}>
                                    {stat.value}
                                </Typography>
                                <Typography variant="body1" color="text.secondary">
                                    {stat.label}
                                </Typography>
                            </Box>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </>
    );
};

RecordInfo.propTypes = {
    record: PropTypes.object.isRequired,
    maxHr: PropTypes.number.isRequired,
    minHr: PropTypes.number.isRequired,
    userData: PropTypes.shape({
        restingHeartRate: PropTypes.number,
        minTrainingHeartRate: PropTypes.number,
        trainingHeartRate: PropTypes.number,
        maxHeartRate: PropTypes.number
    }).isRequired
};

export default RecordInfo;
